import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useForm } from '../../hooks';

import { Link as ReactRouterLink } from 'react-router-dom';
import {
  Link,
  Grid,
  Typography,
  FormControlLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Button, TextField, Checkbox, Form } from '../common/';
import { auth, tokenUtils, emailValidator } from '../../utils';
import FormSubmitted from './FormSubmitted';
import colors from '../../assets/colors';
import texts from '../../assets/texts';

export default function RegisterForm(props) {
  const { handleChange, values } = useForm({
    email: '',
    password: '',
    repeatPassword: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isRegisterSuccessful, setIsRegisterSuccessful] = useState(true);
  const [isRegisterProcessing, setIsRegisterProcessing] = useState(false);
  const [isRepeatPasswordMatching, setIsRepeatPasswordMatching] =
    useState(true);
  const [isFormDataComplete, setIsFormDataComplete] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState({
    termsAndConditionsCheckbox: false,
    marketingCheckbox: false,
    permissionsGDPRCheckbox: false,
    allPermissionsCheckbox: false,
  });

  useEffect(() => {
    if (
      values.email.length > 0 &&
      values.password.length > 0 &&
      values.repeatPassword.length > 0 &&
      emailValidator(values.email) &&
      isCheckboxChecked.termsAndConditionsCheckbox &&
      isCheckboxChecked.marketingCheckbox &&
      isCheckboxChecked.permissionsGDPRCheckbox
    ) {
      setIsFormDataComplete(true);
    } else {
      setIsFormDataComplete(false);
    }
  }, [
    isCheckboxChecked.marketingCheckbox,
    isCheckboxChecked.termsAndConditionsCheckbox,
    isCheckboxChecked.permissionsGDPRCheckbox,
    values.email,
    values.email.length,
    values.password.length,
    values.repeatPassword.length,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (values.password !== values.repeatPassword) {
      setIsRepeatPasswordMatching(false);
      return;
    }
    setIsRegisterProcessing(true);
    setIsRepeatPasswordMatching(true);
    const body = {
      username: values.email,
      password: values.password,
      permissionGDPR: isCheckboxChecked.permissionsGDPRCheckbox,
      permissionMarketing: isCheckboxChecked.marketingCheckbox,
      permissionTerms: isCheckboxChecked.termsAndConditionsCheckbox,
    };
    auth
      .register(body)
      .then((result) => {
        tokenUtils.setToken(result);
        setIsRegisterSuccessful(true);
        setIsRegisterProcessing(false);
        setSubmitted(true);
      })
      .catch(() => {
        setIsRegisterSuccessful(false);
        setIsRegisterProcessing(false);
      });
  };

  const handleCheckboxChange = (event) => {
    setIsCheckboxChecked({
      ...isCheckboxChecked,
      [event.target.name]: event.target.checked,
    });
    if (event.target.name === 'allPermissionsCheckbox') {
      if (event.target.checked) {
        setAllCheckboxesTrue();
      } else {
        setAllCheckboxesFalse();
      }
    }
  };

  const setAllCheckboxesTrue = () => {
    setIsCheckboxChecked({
      termsAndConditionsCheckbox: true,
      marketingCheckbox: true,
      permissionsGDPRCheckbox: true,
      allPermissionsCheckbox: true,
    });
  };

  const setAllCheckboxesFalse = () => {
    setIsCheckboxChecked({
      termsAndConditionsCheckbox: false,
      marketingCheckbox: false,
      permissionsGDPRCheckbox: false,
      allPermissionsCheckbox: false,
    });
  };

  const theme = useTheme();

  return (
    <>
      {!submitted ? (
        <>
          <Typography variant="h4" sx={{ mt: 8, fontWeight: 600 }}>
            Zarejestruj się
          </Typography>
          <Form noValidate>
            <TextField
              login
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="ADRES E-MAIL"
              name="email"
              autoComplete="email"
              onChange={handleChange('email')}
              autoFocus
              error={values.email.length > 0 && !emailValidator(values.email)}
              helperText={
                values.email.length > 0 &&
                !emailValidator(values.email) &&
                'Nieprawidłowy adres email!'
              }
              sx={{ mt: 4 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'email' }}
            />
            <TextField
              login
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="HASŁO"
              type="password"
              id="password"
              onChange={handleChange('password')}
              autoComplete="current-password"
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'password' }}
            />
            <TextField
              login
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="repeatPassword"
              label="POWTÓRZ HASŁO"
              type="password"
              id="repeatPassword"
              onChange={handleChange('repeatPassword')}
              error={!isRepeatPasswordMatching || !isRegisterSuccessful}
              helperText={
                !isRepeatPasswordMatching
                  ? 'Hasła nie sa identyczne'
                  : !isRegisterSuccessful && 'Rejestracja nie powiodła się'
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'repeatPassword' }}
            />
            <Accordion
              sx={{
                marginTop: 2,
                fontSize: 10,
                border: `1px solid ${colors.agroweGreen}`,
                borderRadius: '4px',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label="Expand"
                aria-controls="additional-actions1-content"
                id="additional-actions1-header"
              >
                <FormControlLabel
                  aria-label="All permisions"
                  onClick={(event) => event.stopPropagation()}
                  onFocus={(event) => event.stopPropagation()}
                  control={
                    <Checkbox
                      value="allPermissionsCheckbox"
                      onChange={handleCheckboxChange}
                      checked={isCheckboxChecked.allPermissionsCheckbox}
                      name="allPermissionsCheckbox"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 19 }}>
                      Akceptuję wszystkie zgody
                    </Typography>
                  }
                />
              </AccordionSummary>
              <AccordionDetails sx={{ fontSize: 5 }}>
                <Grid container>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ marginTop: 0, marginLeft: '4%' }}
                      aria-label="Terms and conditions"
                      control={
                        <Checkbox
                          required
                          value="termsAndConditionsCheckbox"
                          onChange={handleCheckboxChange}
                          checked={isCheckboxChecked.termsAndConditionsCheckbox}
                          name="termsAndConditionsCheckbox"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 18 }}>
                          Wyrażam zgodę na warunki korzystania z serwisu *
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ marginTop: 1, marginLeft: '4%' }}
                      required
                      aria-label="Permissions"
                      control={
                        <Checkbox
                          value="permissionsGDPRCheckbox"
                          onChange={handleCheckboxChange}
                          checked={isCheckboxChecked.permissionsGDPRCheckbox}
                          name="permissionsGDPRCheckbox"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 18 }}>
                          Wyrażam zgodę na przetwarzanie moich danych osobowych
                          *
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      sx={{ marginTop: 1, marginLeft: '4%' }}
                      aria-label="Marketing"
                      control={
                        <Checkbox
                          value="marketingCheckbox"
                          required
                          onChange={handleCheckboxChange}
                          checked={isCheckboxChecked.marketingCheckbox}
                          name="marketingCheckbox"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 18 }}>
                          Wyrażam zgodę na otrzymywanie ofert marketingowych *
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ marginTop: 2 }}>
                  POLA OZNACZONE * SĄ WYMAGANE
                </Typography>
              </Grid>
            </Grid>

            <Button
              login
              type="submit"
              fullWidth
              variant="outlined"
              disabled={!isFormDataComplete}
              onClick={handleSubmit}
              sx={{ margin: theme.spacing(3, 0, 2) }}
            >
              {isRegisterProcessing ? (
                <CircularProgress sx={{ color: colors.white }} size={30} />
              ) : (
                'Utwórz konto'
              )}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link
                  href="https://app.agrowe.pl/app/terms"
                  variant="body2"
                  sx={{ color: colors.oldAgroweGreen }}
                >
                  Warunki korzystania i regulamin
                </Link>
              </Grid>
              <Grid item>
                <Link
                  component={ReactRouterLink}
                  to="/login"
                  variant="body2"
                  sx={{ color: colors.oldAgroweGreen }}
                >
                  {'Posiadasz już konto? Zaloguj się!'}
                </Link>
              </Grid>
            </Grid>
          </Form>
        </>
      ) : (
        <FormSubmitted
          documentTitle={props.documentTitle}
          title="Rejestracja powiodła się"
          description={texts.registeredSuccessfully}
          buttonText="Wróć do strony głównej"
        />
      )}
    </>
  );
}
